import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  notificationProviderCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { notificationEndpoints } from "@/features/notification/api/endpoints";
import type {
  NotificationProviderDto,
  NotificationProviderUpdateRequest,
} from "@/features/notification/api/dto";

interface UpdateProviderArgs {
  notificationProviderId: string;
  body: NotificationProviderUpdateRequest;
}

/**
 * Met à jour une ligne de routage (`PUT /NotificationProviders/{id}`, guide
 * v2.3 §6.7). Attention : désactiver le dernier fournisseur actif d'un canal
 * fait échouer tous les envois de ce canal (FAILED journalisés).
 */
export const useUpdateNotificationProvider = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["notification", "providers", "update"],
    mutationFn: ({ notificationProviderId, body }: UpdateProviderArgs) =>
      mutateData({
        url: notificationEndpoints.providers.update(notificationProviderId),
        reqBody: body as unknown as Record<string, unknown>,
        method: "PUT",
      }) as Promise<NotificationProviderDto>,
    onSuccess: (_data, variables) =>
      invalidateAfterMutation(
        queryClient,
        notificationProviderCacheKeys(variables.notificationProviderId)
      ),
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useUpdateNotificationProvider failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "notification" }
      );
    },
  });
};
